import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { faWandMagicSparkles } from '@fortawesome/pro-light-svg-icons/faWandMagicSparkles';
import { TypedMemo } from '../../common/types';
import {
  useFetchAccountStatsQuery,
  useFetchSmartLinkStatsSummariesQuery,
  useGetCatalogQuery,
} from '../../../redux/api/fymAPI';
import { SMLCreationSheet } from '../../smartLinks/creationSheet/SMLCreationSheet';
import { catalogCreationSource } from '../../smartLinks/catalog/catalogCreationSource';
import { HomeBlock } from './HomeBlock';
import { HomeBlockSkeleton } from './HomeBlockSkeleton';
import { HomeSetupCard } from './HomeSetupCard';
import { SmartlinksPerfTop } from './SmartlinksPerfTop';
import { SmartlinksDayBars } from './SmartlinksDayBars';
import { SmartlinksTopLists } from './SmartlinksTopLists';
import { artistHomeMessages as messages } from './artistHomeMessages';
import { pickLatestRelease } from './catalogSummary';
import { HomePeriodDays } from './homePeriod';
import { useHomeSmartLinks } from './useHomeSmartLinks';

interface SmartlinksBlockProps {
  accountId: number;
  days: HomePeriodDays;
  onOpenSmartlinks: () => void;
}

/**
 * The Smartlinks performance block (design a1): click-through, visitors, pre-saves, views per day
 * and the three top lists, all for the Home period. With no smartlink it shows "Create your first
 * smartlink", which opens the creation sheet on the latest release (design a2).
 */
const SmartlinksBlockComponent: React.FC<SmartlinksBlockProps> = ({ accountId, days, onOpenSmartlinks }) => {
  const intl = useIntl();
  const stats = useFetchAccountStatsQuery({ accountId, days });
  const summaries = useFetchSmartLinkStatsSummariesQuery({ accountId, days });
  const smartLinks = useHomeSmartLinks(accountId);
  // The same read as the Catalog block, so the cache answers it once.
  const { data: catalog } = useGetCatalogQuery({ accountId });
  const [isCreateOpen, setCreateOpen] = useState(false);

  const latest = useMemo(() => pickLatestRelease(catalog?.items ?? [], new Date()), [catalog]);
  const creationSource = useMemo(() => catalogCreationSource(latest), [latest]);

  const retry = useCallback(() => {
    stats.refetch().catch(console.error);
    summaries.refetch().catch(console.error);
  }, [stats, summaries]);
  const openCreate = useCallback(() => setCreateOpen(true), []);
  const closeCreate = useCallback(() => setCreateOpen(false), []);

  const title = intl.formatMessage(messages.smartlinksTitle);
  const hasSmartLinks = (smartLinks.total ?? 0) > 0;

  if (stats.isLoading || summaries.isLoading || smartLinks.isLoading) {
    return (
      <HomeBlock icon={faWandMagicSparkles} title={title}>
        <HomeBlockSkeleton />
      </HomeBlock>
    );
  }

  const { data } = stats;

  return (
    <HomeBlock
      icon={faWandMagicSparkles}
      title={title}
      moreLabel={hasSmartLinks ? intl.formatMessage(messages.smartlinksOpen) : undefined}
      onMore={onOpenSmartlinks}
      error={stats.error ?? summaries.error}
      retry={retry}
    >
      {!hasSmartLinks || data === undefined ? (
        <HomeSetupCard
          icon={faWandMagicSparkles}
          title={intl.formatMessage(messages.smartlinksEmptyTitle)}
          body={intl.formatMessage(messages.smartlinksEmptyBody)}
          cta={intl.formatMessage(messages.smartlinksEmptyCta)}
          onAction={openCreate}
          isBare
        />
      ) : (
        <>
          <SmartlinksPerfTop
            totals={data.stats.totals}
            previous={data.previousTotals}
            chart={<SmartlinksDayBars series={data.stats.series} />}
          />
          <SmartlinksTopLists
            stats={data.stats}
            summaries={summaries.data?.summaries ?? []}
            nameById={smartLinks.nameById}
          />
        </>
      )}
      <SMLCreationSheet
        accountId={accountId}
        isOpen={isCreateOpen}
        closeModal={closeCreate}
        initialCatalogTrackMaid={creationSource.catalogTrackMaid}
        initialCatalogItemUuid={creationSource.catalogItemUuid}
      />
    </HomeBlock>
  );
};

export const SmartlinksBlock = TypedMemo(SmartlinksBlockComponent);
